/**
 * Utilitários monetários.
 *
 * Política de arredondamento: o cálculo interno mantém precisão total e o arredondamento acontece
 * apenas na fronteira de apresentação, ao construir um `Valor`. É o comportamento do legado e é o
 * que reproduz os casos de referência de `docs/regras-negocio.md`, seção 10.
 *
 * Decisão pendente P1: avaliar aritmética de inteiros em centavos. A decisão D6 (recálculo no
 * servidor) torna qualquer divergência de arredondamento entre cliente e servidor um erro visível,
 * o que aumenta a importância dessa escolha. Enquanto P1 estiver aberta, o único ponto de
 * arredondamento é `arredondar`, o que mantém a mudança futura localizada.
 */

import type { Valor } from './tipos.ts';

/**
 * Arredonda para o número de casas indicado, meio para cima, corrigindo o viés de representação
 * binária. `Math.round(1.005 * 100) / 100` devolve 1 em vez de 1.01; o ajuste por notação
 * exponencial evita esse caso.
 */
export function arredondar(valor: number, casas = 2): number {
  if (!Number.isFinite(valor)) return valor;
  const ajustado = Number(`${valor}e${casas}`);
  if (!Number.isFinite(ajustado)) {
    const fator = 10 ** casas;
    return Math.round(valor * fator) / fator;
  }
  return Number(`${Math.round(ajustado)}e-${casas}`);
}

/** Constrói um valor com as duas moedas resolvidas. `brl` fica `null` quando não há PTAX. */
export function valorDeUsd(usd: number, ptax: number | null): Valor {
  return {
    usd: arredondar(usd),
    brl: ptax === null ? null : arredondar(usd * ptax),
  };
}

/**
 * Soma dois valores mantendo a coerência entre as moedas.
 *
 * `brl` só é resolvido quando ambos os lados têm conversão; um `null` propaga. Somar apenas o lado
 * convertido produziria um total em real menor que a verdade, sem qualquer sinal de que faltou
 * parcela — o tipo de erro que só aparece quando o cliente confere a conta.
 */
export function somarValores(a: Valor, b: Valor): Valor {
  return {
    usd: arredondar(a.usd + b.usd),
    brl: a.brl === null || b.brl === null ? null : arredondar(a.brl + b.brl),
  };
}

/** Converte um montante de BRL para USD. */
export function brlParaUsd(brl: number, ptax: number): number {
  return brl / ptax;
}

/** Converte um montante de USD para BRL. */
export function usdParaBrl(usd: number, ptax: number): number {
  return usd * ptax;
}

const FORMATO = { minimumFractionDigits: 2, maximumFractionDigits: 2 } as const;

/**
 * Formata em dólar com o prefixo padronizado `US$`.
 * O legado tem cinco variantes de prefixo; `design-system.md` fixa `US$` e `R$`, com espaço.
 */
export function formatarUsd(valor: number): string {
  return `US$ ${valor.toLocaleString('pt-BR', FORMATO)}`;
}

/** Formata em real com o prefixo padronizado `R$`. */
export function formatarBrl(valor: number): string {
  return `R$ ${valor.toLocaleString('pt-BR', FORMATO)}`;
}

/** Formata um percentual com uma casa decimal, como o legado exibe a economia. */
export function formatarPercentual(valor: number): string {
  return `${valor.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
}
